import { Button, Tooltip, message } from "antd";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import html2pdf from "html2pdf.js";
import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

export function ExportToPDF({ participant, calls }) {

    const generatePDFDocument = async () => {
        try {
            message.loading("Generando documento PDF...", 1);

            const container = document.createElement("div");
            container.innerHTML = getHTMLDocument();

            await html2pdf()
                .set({
                    margin: [20, 15],
                    filename: `Historial_Llamadas_${participant.nombres}_${participant.apellidos}_${new Date().toISOString().split("T")[0]}.pdf`,
                    image: { type: "jpeg", quality: 0.98 },
                    html2canvas: {
                        scale: 2,
                        useCORS: true
                    },
                    jsPDF: {
                        unit: "mm",
                        format: "a4",
                        orientation: "portrait"
                    }
                })
                .from(container)
                .save();

            message.success("Documento PDF generado exitosamente");
        } catch (error) {
            console.error(error);
            message.error("Error al generar el PDF");
        }
    };

    const getStatusStyle = (id) => {
        const statusId = Number(id);

        const styles = {
            1: 'background:#E8F4FD;color:#1890FF;border:1px solid #91D5FF;',
            2: 'background:#F6FFED;color:#52C41A;border:1px solid #B7EB8F;',
            3: 'background:#FFF2F0;color:#FF4D4F;border:1px solid #FFCCC7;',
            4: 'background:#FAFAFA;color:#595959;border:1px solid #D9D9D9;'
        };

        return styles[statusId] || styles[4];
    };


    /* =========================
       HTML COMPLETO (MISMO CONTENIDO)
    ========================= */
    const getHTMLDocument = () => `
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8" />
<title>Historial de Llamadas</title>

<style>
@page {
  margin: 20mm 15mm;
}

body {
  font-family: 'Calibri','Segoe UI',Arial,sans-serif;
  font-size: 11pt;
  color: #2C3E50;
  line-height: 1.4;
}

/* =========================
   ENCABEZADO
========================= */
.document-header {
  border-bottom: 3px solid #2C5FAA;
  padding-bottom: 15px;
  margin-bottom: 25px;
}

.document-title {
  font-size: 18pt;
  font-weight: bold;
  color: #2C5FAA;
  text-align: center;
  margin: 0;
}

.document-subtitle {
  font-size: 12pt;
  color: #5D6D7E;
  text-align: center;
  margin: 5px 0 10px;
}

.document-header-meta {
  display: flex;
  justify-content: space-between;
  font-size: 10pt;
  color: #7F8C8D;
}

/* =========================
   SECCIONES
========================= */
.section {
  margin-bottom: 25px;
  page-break-inside: avoid;
}

.section-title {
  font-size: 12pt;
  font-weight: bold;
  color: #2C5FAA;
  border-bottom: 2px solid #EAECEE;
  padding-bottom: 6px;
  margin-bottom: 12px;
}

/* =========================
   INFO PARTICIPANTE
========================= */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px 20px;
  background: #FAFBFD;
  border: 1px solid #EAECEE;
  border-radius: 8px;
  padding: 15px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed #EAECEE;
  padding: 6px 0;
}

.info-label {
  font-weight: bold;
}

.info-value {
  text-align: right;
}

/* =========================
   TARJETAS DE LLAMADA
========================= */
.call-card {
  border: 1px solid #E8E8E8;
  border-radius: 8px;
  margin: 20px 0;
  background: white;
  page-break-inside: avoid;
}

.call-header {
  background: linear-gradient(135deg,#F8FAFD,#F0F5FF);
  padding: 15px;
  display: flex;
  justify-content: space-between;
}

.call-number {
  font-weight: bold;
  color: #2C5FAA;
}

.call-meta {
  display: flex;
  gap: 15px;
}

.call-details {
  padding: 15px;
}

/* =========================
   FOOTER LLAMADA
========================= */
.call-footer {
  background: #FAFAFA;
  border-top: 1px solid #E8E8E8;
  
  padding: 10px 15px;
  font-size: 9pt;
  display: flex;
  justify-content: space-between;
}

/* =========================
   RESUMEN
========================= */
.resumen-grid {
  display: grid;
  grid-template-columns: repeat(3,1fr);
  gap: 15px;
}

.resumen-item {
  border: 1px solid #E8E8E8;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
}

.resumen-value {
  font-size: 15pt;
  font-weight: bold;
  color: #2C5FAA;
}

.resumen-value2 {
    font-size: 10pt;
    font-weight: bold;
    color: #2C5FAA;
  }
</style>
</head>

<body>
${generateDocumentContent()}
</body>
</html>
`;

    /* =========================
       CONTENIDO (MISMA FUNCIÓN)
    ========================= */
    const generateDocumentContent = () => {
        if (!participant || !calls?.length) return "<p>No hay datos disponibles</p>";

        const lastCall = calls[calls.length - 1];
        const estadoActual = lastCall?.estadoLlamadaNombre || 'Sin estado';

        return `
<div class="document-header">
  <h1 class="document-title">📋 HISTORIAL DE LLAMADAS</h1>
  <h2 class="document-subtitle">Proceso de Validación</h2>
  <div class="document-header-meta">
    <span><strong>Bodecom</strong></span>
    <span>Generado el ${formatFecha(new Date(), 'full')}</span>
  </div>
</div>

<div class="section">
  <h2 class="section-title">👤 Información del Participante</h2>
  <div class="info-grid">
    <div class="info-row"><span class="info-label">Nombre:</span><span class="info-value">${participant.nombres} ${participant.apellidos}</span></div>
    <div class="info-row"><span class="info-label">Documento:</span><span class="info-value">${participant.tipo_documento_nombre} ${participant.num_identificacion}</span></div>
    <div class="info-row"><span class="info-label">Teléfono:</span><span class="info-value">${participant.telefono || 'No registrado'}</span></div>
    <div class="info-row"><span class="info-label">Correo:</span><span class="info-value">${participant.correo || 'No registrado'}</span></div>
    <div class="info-row"><span class="info-label">Ubicación registrada:</span><span class="info-value"><span class="info-value">${participant.municipio}, ${participant.departamento}</span></span></div>
    <div class="info-row"><span class="info-label">Estado actual:</span>
    <span class="status-badge"
    style="padding:3px 12px;border-radius:15px;font-weight:bold;${getStatusStyle(lastCall?.estadoLlamadaId)}">
${estadoActual}
</span>

    </div>

    
  </div>
</div>

<div class="section">
  <h2 class="section-title">📞 Detalle de las llamadas</h2>

  <table width="100%" cellspacing="0" cellpadding="0"
    style="
      border-collapse: collapse;
      width: 100%;
      font-size: 10.5pt;
    "
  >

    ${calls.map(call => `
      <!-- BLOQUE DE UNA LLAMADA -->
      <tbody style="page-break-inside: avoid;">
        
        <!-- CABECERA -->
        <tr>
          <td colspan="4"
            style="
              background:#F0F5FF;
              border:1px solid #E8E8E8;
              padding:8px 10px;
              font-weight:bold;
              color:#2C5FAA;
            "
          >
            ${getIntentoLabel(call.numeroIntento)}
            <span style="float:right;font-weight:normal;color:#555;">
              📅 ${formatFecha(call.fechaContacto, 'long')}
              &nbsp;&nbsp;🕒 ${formatHora(call.hora_llamada)}
            </span>
          </td>
        </tr>

        <!-- RESULTADO -->
        <tr>
          <td style="width:22%;padding:6px;border-left:1px solid #E8E8E8;">
            <strong>Resultado</strong>
          </td>
          <td colspan="3" style="padding:6px;border-right:1px solid #E8E8E8;">
            <span
              style="
                display:inline-block;
                padding:3px 10px;
                border-radius:15px;
                font-weight:bold;
                ${getStatusStyle(call.estadoLlamadaId)}
              "
            >
              ${call.estadoLlamadaNombre}
            </span>
          </td>
        </tr>

        <!-- SUBESTADO -->
        ${call.subestadoLlamadaNombre ? `
          <tr>
            <td style="padding:6px;border-left:1px solid #E8E8E8;">
              <strong>Detalle específico</strong>
            </td>
            <td colspan="3" style="padding:6px;border-right:1px solid #E8E8E8;">
              ${call.subestadoLlamadaNombre}
            </td>
          </tr>
        ` : ''}

        <!-- ELEGIBILIDAD -->
        ${call.esElegible !== null && call.esElegible !== undefined ? `
          <tr>
            <td style="padding:6px;border-left:1px solid #E8E8E8;">
              <strong>Elegibilidad</strong>
            </td>
            <td colspan="3" style="padding:6px;border-right:1px solid #E8E8E8;">
              <span
                style="
                  display:inline-block;
                  padding:3px 10px;
                  border-radius:15px;
                  font-weight:bold;
                  ${call.esElegible
                    ? 'background:#F6FFED;color:#52C41A;border:1px solid #B7EB8F;'
                    : 'background:#FFF2F0;color:#FF4D4F;border:1px solid #FFCCC7;'}
                "
              >
                ${call.esElegible ? 'Sí es elegible' : 'No es elegible'}
              </span>
            </td>
          </tr>

          ${!call.esElegible && call.motivoNoElegibleNombre ? `
            <tr>
              <td style="padding:6px;border-left:1px solid #E8E8E8;">
                <strong>Motivo</strong>
              </td>
              <td colspan="3" style="padding:6px;border-right:1px solid #E8E8E8;">
                ${call.motivoNoElegibleNombre}
              </td>
            </tr>
          ` : ''}
        ` : ''}

        <!-- OBSERVACIÓN GENERAL -->
        ${call.observacionGeneral ? `
          <tr>
            <td style="padding:6px;border-left:1px solid #E8E8E8;vertical-align:top;">
              <strong>Observación</strong>
            </td>
            <td colspan="3"
              style="
                padding:6px;
                border-right:1px solid #E8E8E8;
                background:#F9F9F9;
              "
            >
              ${call.observacionGeneral}
            </td>
          </tr>
        ` : ''}

        <!-- FOOTER -->
        <tr>
          <td colspan="4"
            style="
              border:1px solid #E8E8E8;
              border-top:none;
              padding:6px 10px;
              font-size:9pt;
              color:#666;
              background:#FAFAFA;
            "
          >
            👤 Registrado por: ${call.usuarioId || 'Sistema'}
          </td>
        </tr>

        <!-- SEPARADOR VISUAL -->
        <tr><td colspan="4" style="height:12px;"></td></tr>

      </tbody>
    `).join("")}

  </table>
</div>


<div class="section">
  <h2 class="section-title">📊 Resumen del Proceso</h2>

  <div class="resumen-grid">

    <!-- Total llamadas -->
    <div class="resumen-item">
      <div>Total llamadas</div>
      <div class="resumen-value">${calls.length}</div>
    </div>

    <!-- Estado final -->
    <div class="resumen-item">
      <div>Estado final</div>
      <div class="resumen-value">
        <span class="status-badge"
          style="
            display:inline-block;
            padding:3px 12px;
            border-radius:15px;
            font-weight:bold;
            ${getStatusStyle(lastCall?.estadoLlamadaId)}
          ">
          ${lastCall.estadoLlamadaNombre}
        </span>
      </div>
    </div>

    <!-- Última fecha -->
    <div class="resumen-item">
      <div>Última fecha</div>
      <div class="resumen-value2">
        ${formatFecha(lastCall.fechaContacto, 'long')}
      </div>
    </div>

  </div>
</div>


</div>
`;
    };

    /* =========================
       HELPERS
    ========================= */
    const formatFecha = (fecha, format = 'long') => {
        if (!fecha) return 'No registrada';
        const date = new Date(fecha);

        if (format === 'long') {
            return date.toLocaleDateString('es-ES', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        }

        return date.toLocaleString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatHora = (hora) => {
        if (!hora) return 'No registrada';
        return dayjs(hora, 'HH:mm:ss').format('hh:mm A');
    };

    const getIntentoLabel = (n) =>
        ({ 1: 'PRIMERA LLAMADA', 2: 'SEGUNDA LLAMADA', 3: 'TERCERA LLAMADA' }[n] || `LLAMADA ${n}`);

    return (
        <Tooltip title="Exportar historial completo a PDF">
            <Button
                danger
                icon={<FaFilePdf />}
                onClick={generatePDFDocument}
                style={{
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px"
                }}
            >
                <FaDownload size={12} /> Exportar a PDF
            </Button>
        </Tooltip>
    );
}
